import { useEffect, useState, useRef } from "react"

// libraries
import { Rating, Button } from "@mui/material"
import StarIcon from "@mui/icons-material/Star"

// helpers
import Api from "@/_helpers/Api"

// providers
import { trans } from "@/_providers/Translation"
import Skeleton from "react-loading-skeleton"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = {
  date_from: null,
  date_to: null,
  technician_id: "",
  category_id: "",
  brand_id: "",
}

export default function EmployeesRating() {
  const [state, setState] = useState({
    loading: true,
    data: {},
  })
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [technicians, setTechnicians] = useState([])
  const [loadingTechnicians, setLoadingTechnicians] = useState(true)
  const [categories, setCategories] = useState([])
  const [brands, setBrands] = useState([])

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  const stackedData = ["#EE8081", "#F4C165", "#F4DD87", "#FDF28C", "#BEDDC9"]

  const loadData = (currentFilters) => {
    setState((prev) => ({ ...prev, loading: true }))

    const f = `${filters.date_from ? `?date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `${filters.date_from ? "&" : "?"}date_to=${filters.date_to || ""}` : ""}${`${filters.date_from || filters.date_to ? "&" : "?"}technician_id=${filters.technician_id || ""}`}&category_id=${filters.category_id || ""}&brand_id=${filters.brand_id || ""}`

    Api.get(`analytics/technician-reviews${f}`)
      .then((res) => {
        setState({
          loading: false,
          data: res.data,
        })
      })
      .catch(() => setState({ loading: false, data: null }))
  }

  function fetchTechnicians() {
    setLoadingTechnicians(true)

    Api.get("analytics/technicians")
      .then((res) => {
        setTechnicians(res.data || [])
      })
      .catch(() => setTechnicians([]))
      .finally(() => {
        setLoadingTechnicians(false)
      })
  }

  useEffect(() => {
    fetchTechnicians()
    fetchCategories()
    fetchBrands()
  }, [])

  function fetchCategories() {
    Api.get("partners/categories/all")
      .then((res) => {
        setCategories(res.data || [])
      })
      .catch((err) => {
        console.error("Error fetching categories:", err)
      })
  }

  function fetchBrands() {
    Api.get("partners/brands/all")
      .then((res) => {
        setBrands(res.data || [])
      })
      .catch((err) => {
        console.error("Error fetching brands:", err)
      })
  }

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData(filters)
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    return filters.date_from || filters.date_to ? 1 : 0
  }

  function getTotalResults() {
    return Object.values(state.data?.distribution || {}).reduce(
      (sum, count) => sum + count,
      0,
    )
  }

  return (
    <div className="employees-rating">
      <div className="head">
        <h3>{trans("analytics.technicianRatings")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                {loadingTechnicians ? (
                  <Skeleton height={40} width={140} count={1} />
                ) : (
                  <select
                    value={filters.technician_id || ""}
                    name="technician_id"
                    onChange={(e) =>
                      handleFilterChange("technician_id", e.target.value)
                    }
                  >
                    <option value="">{trans("filters.allTechnicians")}</option>
                    {technicians.map((technician) => (
                      <option key={technician.id} value={technician.id}>
                        {technician.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.category_id || ""}
                  name="category_id"
                  onChange={(e) =>
                    handleFilterChange("category_id", e.target.value)
                  }
                >
                  <option value="">{trans("filters.allCategories")}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.brand_id || ""}
                  name="brand_id"
                  onChange={(e) =>
                    handleFilterChange("brand_id", e.target.value)
                  }
                >
                  <option value="">{trans("filters.allBrands")}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      <div className="body">
        {state.loading ? (
          <>
            <Skeleton height={200} count={1} />
          </>
        ) : getTotalResults() === 0 ? (
          <div
            className="alert warning"
            style={{
              marginBottom: 0,
              width: "100%",
            }}
          >
            {trans("messages.noDataFound")}
          </div>
        ) : (
          <>
            <p>{trans("analytics.overallScore")}</p>
            <b>{state.data?.average_rate || 0}</b>
            <Rating
              name="text-feedback"
              value={state.data?.average_rate || 0}
              readOnly
              precision={0.1}
              emptyIcon={<StarIcon style={{ color: "#DBDBDB" }} />}
            />
            <div className="stacked-bar-wrapper">
              <div className="stacked-bar-container">
                {(state.data?.chart_data.values || []).map((item, index) => (
                  <div
                    className={`stacked-bar-segment`}
                    style={{
                      width: `${item}%`,
                      backgroundColor: stackedData[index],
                      cursor: "default",
                      opacity: 1,
                    }}
                  />
                ))}
              </div>
            </div>
            <ul className="rating-list">
              <li>
                <div>
                  <StarIcon style={{ color: "#faaf00" }} />1
                </div>
                {state.data?.distribution?.[1] || 0}
              </li>
              <li>
                <div>
                  <StarIcon style={{ color: "#faaf00" }} />2
                </div>
                {state.data?.distribution?.[2] || 0}
              </li>
              <li>
                <div>
                  <StarIcon style={{ color: "#faaf00" }} />3
                </div>
                {state.data?.distribution?.[3] || 0}
              </li>
              <li>
                <div>
                  <StarIcon style={{ color: "#faaf00" }} />4
                </div>
                {state.data?.distribution?.[4] || 0}
              </li>
              <li>
                <div>
                  <StarIcon style={{ color: "#faaf00" }} />5
                </div>
                {state.data?.distribution?.[5] || 0}
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
